import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useMutation, useQuery} from '@apollo/client';
import {Spin} from 'antd';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {
  GET_EXCHANGE_DETAIL,
  REMOVE_STOCK_EXCHANGE,
  buildVenueBreakdownQuery,
  readVenueBreakdown,
} from './resolvers';
import {INSTRUMENT_TYPES} from './useExchangeScope';
import {ExchangeDetailsForm} from './ExchangeDetailsForm';
import {ExchangeContentForm} from './ExchangeContentForm';
import styles from './exchanges.module.scss';

const TABS = [
  {key: 'details', label: 'Details'},
  {key: 'content', label: 'Content'},
];

/**
 * One exchange. Details owns identity, Content owns the localised narrative.
 *
 * Deleting is guarded by the instrument count: delete-and-recreate is
 * survivable by design — market data keys on `code`, not `_id` — but only if
 * whoever presses it knows how many instruments point at the row.
 */
export const ExchangeDetail = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('details');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const {data, loading, refetch} = useQuery(GET_EXCHANGE_DETAIL, {
    variables: {id: [id], language: 'EN'},
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const node = (data?.getExchanges?.data ?? []).find(
    entry => entry.exchange?.id === id,
  );
  const exchange = node?.exchange;

  const {data: breakdownData} = useQuery(
    buildVenueBreakdownQuery([id], INSTRUMENT_TYPES),
    {skip: !id, fetchPolicy: 'cache-first', errorPolicy: 'all'},
  );
  const counts = breakdownData
    ? readVenueBreakdown(breakdownData, [id], INSTRUMENT_TYPES)[id]
    : null;
  const totalReferencing = counts
    ? Object.values(counts).reduce((sum, n) => sum + n, 0)
    : null;

  const [removeExchange, {loading: deleting}] = useMutation(
    REMOVE_STOCK_EXCHANGE,
    {errorPolicy: 'all'},
  );

  useEffect(() => {
    if (!loading && data && !exchange) {
      toast.error('That exchange could not be loaded');
    }
  }, [loading, data, exchange]);

  if (loading && !data) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading…</h1>
      </div>
    );
  }

  if (!exchange) {
    return (
      <div className={styles.empty}>
        This exchange could not be loaded.{' '}
        <span
          role="button"
          className={styles.linkish}
          onClick={() => navigate('/dashboard/exchanges')}>
          Back to exchanges
        </span>
      </div>
    );
  }

  const handleDelete = () =>
    removeExchange({variables: {id}})
      .then(({data: result}) => {
        const payload = result?.removeStockExchange;
        if (payload?.__typename === 'Error')
          return toast.error(payload.message);
        toast.success(`${exchange.code} deleted`);
        setDeleteOpen(false);
        navigate('/dashboard/exchanges');
      })
      .catch(error => toast.error(error.message));

  return (
    <div>
      <div className={shell.head}>
        <div className={shell.headTop}>
          <div>
            <button
              type="button"
              className={shell.back}
              onClick={() => navigate('/dashboard/exchanges')}>
              ← Exchanges
            </button>
            <h1 className={shell.title}>{exchange.stockExchangeName}</h1>
            <div className={shell.sub}>
              {exchange.code}
              {exchange.baseCurrencyCode
                ? ` · ${exchange.baseCurrencyCode}`
                : ''}
              {node?.instrumentCount != null
                ? ` · ${node.instrumentCount.toLocaleString()} live instruments`
                : ''}
            </div>
          </div>
          <div className={shell.headActions}>
            {/* The exchange-documents screens live in the Documents Center,
                which opens its Stock Exchange tab on `?route=stocks`. */}
            <Button
              type="secondary"
              label="Documents"
              onClick={() =>
                navigate('/dashboard/startup/document-category?route=stocks')
              }
            />
            <Button
              type="secondary"
              label="Delete"
              disabled={!pageOperations?.delete}
              onClick={() => setDeleteOpen(true)}
            />
          </div>
        </div>

        <div className={shell.controls}>
          <div className={shell.segmented}>
            {TABS.map(entry => (
              <button
                type="button"
                key={entry.key}
                onClick={() => setTab(entry.key)}
                className={`${shell.segment} ${
                  tab === entry.key ? shell.segmentActive : ''
                }`}>
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {tab === 'details' ? (
        <ExchangeDetailsForm
          exchange={exchange}
          refetch={refetch}
          canUpdate={pageOperations?.update}
        />
      ) : (
        <ExchangeContentForm
          exchange={exchange}
          refetch={refetch}
          canUpdate={pageOperations?.update}
        />
      )}

      <ConfirmModal
        openModal={deleteOpen}
        setOpenModal={() => setDeleteOpen(false)}
        loading={deleting}
        title={`Delete ${exchange.code}`}
        confirmText={
          <span>
            {totalReferencing == null ? (
              <>Checking how many instruments reference this exchange…</>
            ) : (
              <>
                <strong>
                  {totalReferencing.toLocaleString()} instrument
                  {totalReferencing === 1 ? '' : 's'}
                </strong>{' '}
                reference <strong>{exchange.code}</strong>
                {totalReferencing > 0 && (
                  <>
                    {' '}
                    (
                    {INSTRUMENT_TYPES.filter(
                      type => counts?.[type.instrumentKey],
                    )
                      // Labels are used as written — lowercasing turned
                      // "IPOs" into "ipos".
                      .map(
                        type => `${counts[type.instrumentKey]} ${type.label}`,
                      )
                      .join(', ')}
                    )
                  </>
                )}
                . Market data is keyed on the exchange <em>code</em>, not its
                id, so recreating an exchange with the same code restores those
                joins — but nothing else about this row survives.
              </>
            )}
          </span>
        }
        handleOperation={handleDelete}
      />
    </div>
  );
};

export default ExchangeDetail;
